/* ------------------------------------------------------------------
   variant-select.js
   Inline variant dropdown + add-to-cart on product cards.

   Covers two card systems:
     1. Liquid cards  -> snippets/product-card-variant-select.liquid
                         (markup already rendered, this file only wires
                          up change / click behaviour)
     2. SearchTap Vue cards -> assets/searchtap.js emits
                         <a class="dista-vs-anchor" data-dista-variants="{...}">
                         and this file replaces it with a real dropdown.

   Public API (referenced from inline onclick/onchange in Liquid):
     DistaVS.change(event)
     DistaVS.add(event)

   NOTE: BoosterTheme is declared with `const` at the top level of
   booster.js, so it is a global *binding* but NOT a property of window.
   Always reference the bare identifier behind a typeof guard.
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  var ROOT = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/+$/, '');

  /* ---------------- helpers ---------------- */

  function booster() {
    return (typeof BoosterTheme !== 'undefined') ? BoosterTheme : null;
  }

  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (el.matches(sel)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function selectedOption(select) {
    return select && select.options[select.selectedIndex];
  }

  function setButtonState(picker, available) {
    var btn = picker.querySelector('.js-vs-atc');
    if (!btn) return;
    var label = btn.querySelector('[data-button-text]');
    if (available) {
      btn.disabled = false;
      btn.classList.remove('is-soldout');
      if (label) label.innerText = btn.dataset.originalText || 'Add to cart';
    } else {
      btn.disabled = true;
      btn.classList.add('is-soldout');
      if (label) label.innerText = btn.dataset.soldOutText || 'Sold out';
    }
  }

  /* ---------------- price rendering ---------------- */

  /* Liquid cards: prices come pre-formatted from Liquid on each <option>. */
  function updateLiquidPrice(picker, opt) {
    var card = closest(picker, '.card--default');
    if (!card || !opt) return;
    var wrap = card.querySelector('.card__price');
    if (!wrap) return;

    var current = wrap.querySelector('.jsPrice');
    var old = wrap.querySelector('.card__price--old');
    var badge = wrap.querySelector('.card__discount');

    if (current && opt.dataset.price) {
      current.innerText = opt.dataset.price;
      // Let Booster's currency converter re-run over this node.
      delete current.dataset.currency;
    }
    if (old) {
      old.innerText = opt.dataset.compare || '';
      delete old.dataset.currency;
    }
    if (badge) {
      badge.innerText = opt.dataset.discount ? ('-' + opt.dataset.discount + '%') : '';
    }

    var b = booster();
    if (b && b.cfx && typeof b.cfx.convertAll === 'function') {
      try { b.cfx.convertAll({}); } catch (e) {}
    }
  }

  /* SearchTap cards: raw numbers, converted the same way searchtap.js does. */
  function stMoney(value) {
    var fmt = document.body.getAttribute('data-money-format');
    if (typeof fmt !== 'string' || !fmt) fmt = '{{amount_with_comma_separator}}';

    var cur = 'INR';
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      cur = window.Shopify.currency.active;
    } else {
      var parts = document.cookie.split(';');
      for (var i = 0; i < parts.length; i++) {
        var kv = parts[i].trim().split('=');
        if (kv[0] === 'cart_currency') { cur = kv[1]; break; }
      }
    }

    var rates = window.currencyRates || {};
    var info = rates[cur];
    var amount = Number(value) || 0;

    if (info && typeof info.rate === 'number' && !isNaN(info.rate)) {
      amount = amount * info.rate;
      amount = amount * (1 + ((info.increase || 0) / 100));
    }

    var str = amount.toFixed(2).split('.');
    str[0] = str[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    str = str.join('.');

    return fmt
      .replace('{{amount}}', str)
      .replace('{{amount_with_comma_separator}}', str)
      .replace(/{{currency}}/g, cur);
  }

  function updateStPrice(picker, variant) {
    var card = closest(picker, '.st-product');
    if (!card || !variant) return;
    var wrap = card.querySelector('.st-product-price');
    if (!wrap) return;

    var newP = wrap.querySelector('.new-price');
    var oldP = wrap.querySelector('.old-price');

    var disc = (variant.discounted_price != null) ? variant.discounted_price : variant.price;
    if (newP && disc != null) newP.innerText = stMoney(disc);
    if (oldP) {
      if (variant.price != null && disc != null && Number(disc) < Number(variant.price)) {
        oldP.innerText = stMoney(variant.price);
        oldP.style.display = '';
      } else {
        oldP.style.display = 'none';
      }
    }
  }

  /* ---------------- public handlers ---------------- */

  var DistaVS = {

    change: function (event) {
      var select = event.currentTarget || event.target;
      var picker = closest(select, '[data-variant-picker]');
      if (!picker) return;

      var opt = selectedOption(select);
      var available = !opt || opt.dataset.available !== 'false';
      setButtonState(picker, available);

      if (picker.hasAttribute('data-st')) {
        var list = picker._variants || [];
        for (var i = 0; i < list.length; i++) {
          if (String(list[i].id) === String(select.value)) {
            updateStPrice(picker, list[i]);
            break;
          }
        }
      } else {
        updateLiquidPrice(picker, opt);
      }
    },

    add: function (event) {
      // event.currentTarget must be the button: BoosterTheme.cart.addToCartJSON
      // reads it to toggle [data-button-text] and .disabled.
      var btn = event.currentTarget;
      if (!btn || btn.disabled) return;

      var picker = closest(btn, '[data-variant-picker]');
      if (!picker) return;

      var select = picker.querySelector('.js-vs-select');
      var id = select ? select.value : btn.getAttribute('data-variant-id');
      if (!id) return;

      var opt = selectedOption(select);
      if (opt && (opt.disabled || opt.dataset.available === 'false')) return;

      if (event.preventDefault) event.preventDefault();
      if (event.stopPropagation) event.stopPropagation();

      var b = booster();
      if (b && b.cart && typeof b.cart.addToCartJSON === 'function') {
        b.cart.addToCartJSON(event, { items: [{ id: parseInt(id, 10), quantity: 1 }] });
      } else {
        // No JS cart available -> plain permalink add, keeps the market prefix.
        window.location.href = ROOT + '/cart/' + id + ':1';
      }
    }
  };

  window.DistaVS = DistaVS;

  /* ---------------- SearchTap hydration ---------------- */

  function variantLabel(v) {
    if (v.title) return v.title;
    var parts = [v.option1, v.option2, v.option3].filter(function (x) {
      return x && String(x).toLowerCase() !== 'default title';
    });
    return parts.join(' / ') || 'Default';
  }

  function inStock(v) {
    if (typeof v.inventory_quantity === 'number') return v.inventory_quantity > 0;
    if (typeof v.available === 'boolean') return v.available;
    return true;
  }

  function buildStPicker(anchor, data) {
    var variants = (data && data.variants) || [];

    var picker = document.createElement('div');
    picker.className = 'card__variant-picker card__variant-picker--st';
    picker.setAttribute('data-variant-picker', '');
    picker.setAttribute('data-st', '1');
    picker._variants = variants;

    var select = document.createElement('select');
    select.className = 'card__variant-select js-vs-select';
    select.setAttribute('aria-label', 'Select option');

    var firstAvailable = -1;
    variants.forEach(function (v, i) {
      var ok = inStock(v);
      if (ok && firstAvailable === -1) firstAvailable = i;
      var o = document.createElement('option');
      o.value = v.id;
      o.textContent = variantLabel(v) + (ok ? '' : ' \u2014 Sold out');
      o.disabled = !ok;
      o.dataset.available = ok ? 'true' : 'false';
      select.appendChild(o);
    });
    select.selectedIndex = firstAvailable === -1 ? 0 : firstAvailable;
    select.addEventListener('change', function (e) { DistaVS.change(e); });
    select.addEventListener('click', function (e) { e.stopPropagation(); });

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = anchor.className.replace(/\bdista-vs-anchor\b/, '').trim() + ' js-vs-atc';
    btn.dataset.originalText = 'Add to cart';
    btn.dataset.soldOutText = 'Sold out';
    btn.innerHTML = '<span data-button-text>Add to cart</span>';
    btn.addEventListener('click', function (e) { DistaVS.add(e); });

    picker.appendChild(select);
    picker.appendChild(btn);

    setButtonState(picker, firstAvailable !== -1);
    if (variants[select.selectedIndex]) updateStPrice(picker, variants[select.selectedIndex]);

    return picker;
  }

  function hydrate(anchor) {
    var raw = anchor.getAttribute('data-dista-variants');
    if (!raw) return;

    var data;
    try { data = JSON.parse(raw); } catch (e) { return; }
    var variants = (data && data.variants) || [];
    if (variants.length < 2) return;

    var sig = variants.map(function (v) { return v.id; }).join(',');
    if (anchor.getAttribute('data-vs-sig') === sig) return;

    // Rebuild if this DOM node was recycled by Vue for another product.
    var stale = anchor.parentNode && anchor.parentNode.querySelector('.card__variant-picker--st');
    if (stale) stale.parentNode.removeChild(stale);

    anchor.setAttribute('data-vs-sig', sig);
    anchor.parentNode.appendChild(buildStPicker(anchor, data));
  }

  var scanQueued = false;
  function scan() {
    scanQueued = false;
    var nodes = document.querySelectorAll('a.dista-vs-anchor[data-dista-variants]');
    for (var i = 0; i < nodes.length; i++) hydrate(nodes[i]);
  }
  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    (window.requestAnimationFrame || setTimeout)(scan, 16);
  }

  function boot() {
    scan();
    if (!('MutationObserver' in window)) return;
    new MutationObserver(queueScan).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-dista-variants']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // SearchTap re-renders the grid on filter / sort / paginate.
  window.addEventListener('st.collection.paginated', queueScan);
})();
